import React, { useEffect, useState } from "react";
import {
  FaBolt,
  FaLightbulb,
  FaInfoCircle,
  FaPowerOff,
  FaSyncAlt,
  FaTable,
  FaCog,
} from "react-icons/fa";
import ArchiveTableModal from "../../components/Archive/ArchiveTableModal";
import styles from "./Monitor.module.css";

const isActiveValue = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    value === "true" ||
    value === "TRUE" ||
    value === "on" ||
    value === "ON"
  );
};

const getCustomName = (customNames, index) => {
  if (!customNames) return "";

  const name = customNames[index] ?? customNames[String(index)];

  if (name === undefined || name === null || String(name).trim() === "") {
    return "";
  }

  return String(name).trim();
};

const parseRelayList = (value) => {
  if (value === undefined || value === null || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map(Number).filter((n) => Number.isInteger(n));
  }

  return String(value)
    .split(",")
    .map((item) => Number(String(item).trim()))
    .filter((n) => Number.isInteger(n));
};

const getToDOutValue = (item = {}) => {
  const settings = item.settings || {};

  return (
    settings.toDOut ??
    settings.to_dout ??
    settings.toDout ??
    item.toDOut ??
    item.to_dout ??
    item.toDout ??
    ""
  );
};

const getSourceTypeInfo = (type) => {
  switch (type) {
    case "temp":
      return {
        title: "دما",
        fallback: "سنسور دما",
      };

    case "humi":
      return {
        title: "رطوبت",
        fallback: "سنسور رطوبت",
      };

    case "wind":
      return {
        title: "باد",
        fallback: "سنجش باد",
      };

    case "dIn":
      return {
        title: "ورودی دیجیتال",
        fallback: "ورودی دیجیتال",
      };

    default:
      return {
        title: type,
        fallback: type,
      };
  }
};

const getSourceCustomName = (allCustomNames, type, index) => {
  const names = allCustomNames?.[type];

  if (!names) return "";

  const name = names[index] ?? names[String(index)];

  if (name === undefined || name === null || String(name).trim() === "") {
    return "";
  }

  return String(name).trim();
};

const collectActivationSources = (
  allSensorData = {},
  outputIndex,
  allCustomNames = {},
) => {
  const sources = [];

  Object.entries(allSensorData || {}).forEach(([key, item]) => {
    if (!item) return;

    const match = key.match(/^(temp|humi|wind|dIn)(\d+)$/);

    if (!match) return;

    const sourceType = match[1];
    const sourceIndex = Number(match[2]);

    if (!Number.isInteger(sourceIndex)) return;

    const relays = parseRelayList(getToDOutValue(item));

    if (!relays.includes(Number(outputIndex))) return;

    const typeInfo = getSourceTypeInfo(sourceType);
    const customName = getSourceCustomName(
      allCustomNames,
      sourceType,
      sourceIndex,
    );

    sources.push(
      customName
        ? `${customName} (${typeInfo.title} ${sourceIndex})`
        : `${typeInfo.fallback} ${sourceIndex}`,
    );
  });

  return sources;
};

const getActivationHint = (sources) => {
  if (!sources || sources.length === 0) {
    return "بدون اتصال";
  }

  return `متصل به:\n${sources.join("\n")}`;
};

const POWER_COMMAND_MIN_VISIBLE_MS = 700;
const POWER_COMMAND_TARGET_STABLE_MS = 1200;
const POWER_COMMAND_TIMEOUT_MS = 15000;

const getEnableCommandValue = (enabled) => (enabled ? "1" : "0");

const getPowerButtonStyle = (isOn, isClickable, isPending = false) => {
  const baseStyle = {
    width: "50px",
    height: "50px",
    minWidth: "50px",
    minHeight: "50px",
    borderRadius: "18px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    transform: "translateY(-2px)",
    cursor: isPending ? "wait" : isClickable ? "pointer" : "not-allowed",
    opacity: isPending ? 0.92 : isClickable ? 1 : 0.65,
    transition:
      "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease, color 0.18s ease, filter 0.18s ease",
    boxShadow:
      "0 14px 28px rgba(0, 0, 0, 0.34), 0 5px 0 rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.12)",
  };

  if (isPending) {
    return {
      ...baseStyle,
      color: "#73C2FB",
      border: "1px solid rgba(115, 194, 251, 0.46)",
      background:
        "radial-gradient(circle at 30% 20%, rgba(115, 194, 251, 0.24), transparent 42%), linear-gradient(145deg, rgba(31, 64, 104, 0.96), rgba(15, 23, 42, 0.98))",
      filter: "drop-shadow(0 0 10px rgba(115, 194, 251, 0.28))",
      boxShadow:
        "0 14px 28px rgba(0, 0, 0, 0.34), 0 5px 0 rgba(0, 0, 0, 0.20), 0 0 22px rgba(115, 194, 251, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.16)",
    };
  }

  if (isOn) {
    return {
      ...baseStyle,
      color: "#39ff14",
      border: "1px solid rgba(57, 255, 20, 0.42)",
      background:
        "radial-gradient(circle at 30% 20%, rgba(57, 255, 20, 0.24), transparent 42%), linear-gradient(145deg, rgba(31, 64, 104, 0.96), rgba(15, 23, 42, 0.98))",
      filter: "drop-shadow(0 0 8px rgba(57, 255, 20, 0.24))",
    };
  }

  return {
    ...baseStyle,
    color: "#E85D3F",
    border: "1px solid rgba(232, 93, 63, 0.36)",
    background:
      "radial-gradient(circle at 30% 20%, rgba(232, 93, 63, 0.20), transparent 42%), linear-gradient(145deg, rgba(40, 45, 60, 0.92), rgba(15, 23, 42, 0.98))",
    filter: "none",
  };
};

const getPowerIconStyle = () => ({
  width: "22px",
  height: "22px",
  fontSize: "22px",
  lineHeight: 1,
  flex: "0 0 auto",
});

const getBoltIconStyle = () => ({
  width: "18px",
  height: "18px",
  fontSize: "18px",
  lineHeight: 1,
  flex: "0 0 auto",
});

const getInfoIconStyle = (hasConnection) => ({
  width: "18px",
  height: "18px",
  fontSize: "18px",
  lineHeight: 1,
  flex: "0 0 auto",
  color: hasConnection ? "#73C2FB" : "rgba(255, 245, 230, 0.34)",
  filter: hasConnection
    ? "drop-shadow(0 0 6px rgba(115, 194, 251, 0.45))"
    : "none",
});

const DigitalOutputMonitorCard = ({
  type,
  index,
  label,
  icon,
  data,
  onUpdate,
  customNames = {},
  allSensorData = {},
  allCustomNames = {},
  onOpenSettings,
  canControlOutputs = true,
  serial,
  token,
}) => {
  const safeData = data || {};
  const { status, value, settings = {}, triggered } = safeData;

  const outputStatusValue = status ?? value;
  const isOutputActive = isActiveValue(outputStatusValue);
  const isOutputEnabled = isActiveValue(settings.enable);
  const isTriggered = isActiveValue(triggered);

  const [pendingPowerCommand, setPendingPowerCommand] = useState(null);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const isPowerCommandPending = Boolean(pendingPowerCommand);
  const displayedPowerEnabled = isPowerCommandPending
    ? pendingPowerCommand.previousEnabled
    : isOutputEnabled;

  useEffect(() => {
    setPendingPowerCommand(null);
  }, [type, index]);

  useEffect(() => {
    if (!pendingPowerCommand) return undefined;

    const now = Date.now();
    const elapsed = now - pendingPowerCommand.startedAt;
    const targetReached =
      getEnableCommandValue(isOutputEnabled) === pendingPowerCommand.target;

    if (elapsed >= POWER_COMMAND_TIMEOUT_MS) {
      setPendingPowerCommand(null);
      return undefined;
    }

    if (targetReached && !pendingPowerCommand.targetStableSince) {
      setPendingPowerCommand((current) => {
        if (!current || current.id !== pendingPowerCommand.id) return current;
        return { ...current, targetStableSince: now };
      });
      return undefined;
    }

    if (!targetReached && pendingPowerCommand.targetStableSince) {
      setPendingPowerCommand((current) => {
        if (!current || current.id !== pendingPowerCommand.id) return current;
        return { ...current, targetStableSince: null };
      });
      return undefined;
    }

    const stableElapsed =
      targetReached && pendingPowerCommand.targetStableSince
        ? now - pendingPowerCommand.targetStableSince
        : 0;

    if (
      targetReached &&
      elapsed >= POWER_COMMAND_MIN_VISIBLE_MS &&
      stableElapsed >= POWER_COMMAND_TARGET_STABLE_MS
    ) {
      setPendingPowerCommand(null);
      return undefined;
    }

    const remainingMinVisible = Math.max(
      POWER_COMMAND_MIN_VISIBLE_MS - elapsed,
      0,
    );
    const remainingStable = targetReached
      ? Math.max(POWER_COMMAND_TARGET_STABLE_MS - stableElapsed, 0)
      : 250;
    const remainingTimeout = Math.max(POWER_COMMAND_TIMEOUT_MS - elapsed, 80);

    const delay = Math.min(
      Math.max(remainingMinVisible, remainingStable, 80),
      remainingTimeout,
    );

    const timerId = window.setTimeout(() => {
      setPendingPowerCommand((current) => {
        if (!current || current.id !== pendingPowerCommand.id) return current;

        const currentNow = Date.now();
        const currentElapsed = currentNow - current.startedAt;
        const currentStableElapsed = current.targetStableSince
          ? currentNow - current.targetStableSince
          : 0;
        const currentTargetReached =
          getEnableCommandValue(isOutputEnabled) === current.target;

        if (currentElapsed >= POWER_COMMAND_TIMEOUT_MS) return null;

        if (
          currentTargetReached &&
          currentElapsed >= POWER_COMMAND_MIN_VISIBLE_MS &&
          currentStableElapsed >= POWER_COMMAND_TARGET_STABLE_MS
        ) {
          return null;
        }

        return current;
      });
    }, delay);

    return () => window.clearTimeout(timerId);
  }, [pendingPowerCommand, isOutputEnabled, type, index]);

  if (!data) return null;

  const activationSources = collectActivationSources(
    allSensorData,
    index,
    allCustomNames,
  );

  const activationHint = getActivationHint(activationSources);

  const customName = getCustomName(customNames, index);
  const displayName = customName || label;
  const displayTitle = customName ? `${customName} - ${label}` : label;
  const sensorKey = `${type}${index}`;
  const isOfflinePreview = Boolean(
    data?.isOfflinePreview || data?.isLastStatePreview,
  );
  const lastSeenLabel = data?.lastSeenLabel || "";

  const canToggleOutput =
    canControlOutputs && !isOfflinePreview && typeof onUpdate === "function";
  const canClickPowerButton = canToggleOutput && !isPowerCommandPending;
  const pendingPowerText =
    pendingPowerCommand?.target === "1"
      ? "در حال فعال‌سازی خروجی..."
      : "در حال غیرفعال‌سازی خروجی...";

  const handleToggleOutputPower = (e) => {
    e.stopPropagation();

    if (!canClickPowerButton) return;

    const nextEnable = getEnableCommandValue(!isOutputEnabled);

    setPendingPowerCommand({
      id: `${type || "dOut"}-${index}-${Date.now()}`,
      target: nextEnable,
      previousEnabled: isOutputEnabled,
      startedAt: Date.now(),
      targetStableSince: null,
    });

    try {
      const result = onUpdate("enable", nextEnable);

      if (result && typeof result.catch === "function") {
        result.catch((error) => {
          console.error("خطا در ارسال فرمان خروجی:", error);
          setPendingPowerCommand(null);
        });
      }
    } catch (error) {
      console.error("خطا در ارسال فرمان خروجی:", error);
      setPendingPowerCommand(null);
    }
  };

  const handlePowerMouseEnter = (e) => {
    if (!canClickPowerButton) return;

    e.currentTarget.style.transform = "translateY(-5px)";
    e.currentTarget.style.boxShadow = displayedPowerEnabled
      ? "0 18px 36px rgba(0, 0, 0, 0.42), 0 7px 0 rgba(0, 0, 0, 0.26), 0 0 22px rgba(57, 255, 20, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.16)"
      : "0 18px 36px rgba(0, 0, 0, 0.42), 0 7px 0 rgba(0, 0, 0, 0.26), 0 0 20px rgba(232, 93, 63, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.16)";
  };

  const handlePowerMouseLeave = (e) => {
    if (isPowerCommandPending) return;

    e.currentTarget.style.transform = "translateY(-2px)";
    e.currentTarget.style.boxShadow =
      "0 14px 28px rgba(0, 0, 0, 0.34), 0 5px 0 rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.12)";
  };

  return (
    <div
      className={`
        ${styles.sensorCard}
        ${styles.monitorOnlyCard}
        ${isOfflinePreview ? styles.offlinePreviewCard : ""}
      `}
      style={{ position: "relative" }}
    >
      <div className={`${styles.cardHeader} ${styles.sensorMonitorHeader}`}>
        <div className={styles.sensorMonitorHeaderMain}>
          <div className={styles.sensorMonitorTitleBlock}>
            <div className={styles.sensorMonitorTitleTop}>
              <span className={styles.sensorMonitorMainIcon}>{icon}</span>

              <span
                className={`
                  ${styles.sensorMonitorCustomName}
                  ${!customName ? styles.cardDisplayNameDefault : ""}
                `}
                title={displayTitle}
              >
                {displayName}
              </span>
            </div>

            <div className={styles.sensorMonitorInlineActions}>
              {isOfflinePreview && lastSeenLabel && (
                <div
                  className={styles.offlineHeaderMeta}
                  title={`آخرین تغییر: ${lastSeenLabel}`}
                >
                  <span className={styles.offlineHeaderMetaTitle}>
                    آخرین تغییر
                  </span>
                  <span className={styles.offlineHeaderMetaValue}>
                    {lastSeenLabel}
                  </span>
                </div>
              )}

              {typeof onOpenSettings === "function" && (
                <button
                  type="button"
                  className={styles.monitorActionBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenSettings();
                  }}
                  title="تنظیمات این خروجی"
                  aria-label={`تنظیمات ${displayTitle}`}
                >
                  <FaCog />
                </button>
              )}

              <button
                type="button"
                className={styles.monitorActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsArchiveOpen(true);
                }}
                title="مشاهده آرشیو جدولی"
                aria-label="مشاهده آرشیو جدولی"
              >
                <FaTable />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          minHeight: "78px",
          margin: "8px 0 18px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span
          className={styles.digitalOutputIconHint}
          data-hint={
            isPowerCommandPending
              ? pendingPowerText
              : canToggleOutput
                ? isOutputEnabled
                  ? "کلیک برای غیرفعال کردن خروجی"
                  : "کلیک برای فعال کردن خروجی"
                : "شما اجازه کنترل خروجی را ندارید"
          }
        >
          <button
            type="button"
            onClick={handleToggleOutputPower}
            onMouseEnter={handlePowerMouseEnter}
            onMouseLeave={handlePowerMouseLeave}
            style={getPowerButtonStyle(
              displayedPowerEnabled,
              canClickPowerButton,
              isPowerCommandPending,
            )}
            disabled={!canClickPowerButton}
            aria-label={
              isPowerCommandPending
                ? pendingPowerText
                : canToggleOutput
                  ? isOutputEnabled
                    ? "غیرفعال کردن خروجی"
                    : "فعال کردن خروجی"
                  : "کنترل خروجی مجاز نیست"
            }
          >
            {isPowerCommandPending ? (
              <span className={styles.digitalOutputPowerLoading}>
                <FaSyncAlt
                  className={styles.digitalOutputPowerSpinner}
                  style={getPowerIconStyle()}
                />
              </span>
            ) : (
              <FaPowerOff style={getPowerIconStyle()} />
            )}
          </button>
        </span>
      </div>

      <div className={styles.digitalOutputCornerIcons}>
        <span
          className={styles.digitalOutputIconHint}
          data-hint={isOutputActive ? "خروجی فعال" : "خروجی غیرفعال"}
        >
          <FaBolt
            className={`
    ${styles.digitalOutputCornerIcon}
    ${isOutputActive ? styles.digitalOutputBoltOn : styles.digitalOutputBoltOff}
  `}
            style={getBoltIconStyle()}
            aria-label={isOutputActive ? "خروجی فعال" : "خروجی غیرفعال"}
          />
        </span>

        <span
          className={styles.digitalOutputIconHint}
          data-hint={isTriggered ? "Trig فعال" : "Trig غیرفعال"}
        >
          <FaLightbulb
            className={`
              ${styles.digitalOutputCornerIcon}
              ${isTriggered ? styles.cardTrigCornerOn : styles.cardTrigCornerOff}
            `}
            aria-label={isTriggered ? "Trig فعال" : "Trig غیرفعال"}
          />
        </span>

        <span
          className={`
            ${styles.digitalOutputIconHint}
            ${styles.digitalOutputActivationHint}
          `}
          data-hint={activationHint}
        >
          <FaInfoCircle
            className={styles.digitalOutputCornerIcon}
            style={getInfoIconStyle(activationSources.length > 0)}
            aria-label="متصل به"
          />
        </span>
      </div>

      <ArchiveTableModal
        isOpen={isArchiveOpen}
        onClose={() => setIsArchiveOpen(false)}
        serial={serial}
        sensorKey={sensorKey}
        title={displayTitle}
        token={token}
      />
    </div>
  );
};

export default DigitalOutputMonitorCard;
